/**
 * Shared primitives for the Contracts module.
 *
 * Extracted out of the monolithic `ContractsTab.js` so the sub-components
 * (ContractEditDialog, TemplateEditor, SignatoriesList) don't have to be
 * re-imported through the root file.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Parse a Content-Disposition response header and extract the
 * `filename="..."` value. Falls back to the supplied default.
 * Backend is the single source of truth for naming
 * (see `_filename_for_contract` in routers/contracts.py).
 */
export function filenameFromContentDisposition(headerValue, fallback) {
  if (!headerValue) return fallback;
  // RFC 6266 — match the quoted-string form first, then bare token form.
  const m = headerValue.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return (m && m[1]) ? m[1].trim() : fallback;
}

export const STATUS_BADGE = {
  draft: 'bg-slate-100 text-slate-700',
  in_review: 'bg-amber-100 text-amber-700',
  signed: 'bg-green-100 text-green-700',
  terminated: 'bg-red-100 text-red-700',
  archived: 'bg-gray-100 text-gray-500',
};

export const KIND_LABEL = {
  master: 'Master',
  annex: 'Annex',
  support_letter: 'Support Letter',
  client_letter: 'Client Letter',
};

// Fetch a protected image as a blob URL so `<img>` can render without
// needing the Authorization header (browsers don't send it on plain
// <img src>). Used for signatory stamps and template watermarks.
export function ProtectedImage({ url, alt, className, fallbackText }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) { setSrc(null); return; }
    let cancelled = false;
    let blobUrl = null;
    axios.get(url, { responseType: 'blob' })
      .then((r) => {
        if (cancelled) return;
        blobUrl = window.URL.createObjectURL(r.data);
        setSrc(blobUrl);
      })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => {
      cancelled = true;
      if (blobUrl) window.URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  if (!src) {
    return <span className="text-[10px] text-slate-400 italic">{fallbackText || 'loading…'}</span>;
  }
  return <img src={src} alt={alt || ''} className={className} />;
}

export function SignatoryStampPreview({ signatoryId, className }) {
  return (
    <ProtectedImage
      url={`${API}/client-stamp-signatories/${signatoryId}/stamp`}
      alt="stamp"
      className={className || 'h-12 max-w-[160px] object-contain border bg-slate-50'}
      fallbackText="no stamp uploaded"
    />
  );
}
